/**
 * Full-sheet CSV / TSV / JSONL export.
 *
 * The legacy exporter in `shell.ts` only wrote a 200×50 rectangle and was
 * capped to the first two hundred rows. This walks the sheet's reported
 * `usedRange` (set by the Data_Store incrementally) and emits every
 * non-empty cell in document order, streaming chunks so memory stays
 * bounded even for the 5,000,000-row ceiling.
 *
 * Quoting follows RFC 4180: any field containing the delimiter, a quote,
 * CR, or LF is double-quoted, with embedded quotes doubled. Text cells
 * are emitted as-is; numbers and dates use the displayed value the
 * Format_Manager produces, so the output matches what the user sees on
 * screen (Requirement 11.3).
 */

import { EMPTY, type CellValue, type Range, type SheetId } from '../core/types.js';
import type { KernelClient } from '../kernel/client.js';
import { tileCell } from '../kernel/tile.js';

export type ExportFormat = 'csv' | 'tsv' | 'jsonl';

export interface ExportOptions {
  format: ExportFormat;
  sheet: SheetId;
  /** Optional override range; defaults to the sheet's usedRange. */
  range?: Range | undefined;
  /** Maximum rows to export (defaults to 5,000,000). */
  rowLimit?: number | undefined;
  onProgress?: ((info: { rows: number }) => void) | undefined;
}

export interface ExportResult {
  blob: Blob;
  text: string;
  rows: number;
  cells: number;
}

const DEFAULT_ROW_LIMIT = 5_000_000;

/** Exports a sheet to a CSV / TSV / JSONL Blob and string. */
export async function exportSheet(
  client: KernelClient,
  opts: ExportOptions,
  signal?: AbortSignal,
): Promise<ExportResult> {
  const view = await client.sheetView(opts.sheet);
  const used = opts.range ?? view.usedRange;
  const r0 = used?.r0 ?? 0;
  const c0 = used?.c0 ?? 0;
  const r1 = used?.r1 ?? -1;
  const c1 = used?.c1 ?? -1;
  const rowLimit = opts.rowLimit ?? DEFAULT_ROW_LIMIT;
  const effectiveR1 = Math.min(r1, r0 + rowLimit - 1);
  const mime =
    opts.format === 'jsonl'
      ? 'application/x-ndjson'
      : opts.format === 'tsv'
        ? 'text/tab-separated-values'
        : 'text/csv';

  if (effectiveR1 < r0) {
    return { blob: new Blob([], { type: `${mime};charset=utf-8;` }), text: '', rows: 0, cells: 0 };
  }

  const parts: string[] = [];
  let totalCells = 0;
  const CHUNK_ROWS = 1024;

  for (let chunkStart = r0; chunkStart <= effectiveR1; chunkStart += CHUNK_ROWS) {
    if (signal?.aborted) break;
    const chunkEnd = Math.min(effectiveR1, chunkStart + CHUNK_ROWS - 1);
    const range: Range = { sheet: opts.sheet, r0: chunkStart, c0, r1: chunkEnd, c1 };
    const tile = await client.tile(range);
    const lines: string[] = [];
    for (let row = chunkStart; row <= chunkEnd; row++) {
      const fields: string[] = [];
      let rowHasValue = false;
      for (let col = c0; col <= c1; col++) {
        const v = readCell(tile, row, col);
        const text = formatCell(v);
        if (text.length > 0) rowHasValue = true;
        fields.push(text);
        if (text.length > 0) totalCells += 1;
      }
      // Skip wholly blank rows so the file is tight.
      if (!rowHasValue) continue;
      lines.push(formatLine(fields, opts.format));
    }
    if (lines.length > 0) {
      if (parts.length > 0) parts.push('\n');
      parts.push(lines.join('\n'));
    }
    opts.onProgress?.({ rows: chunkEnd - chunkStart + 1 });
  }

  const blob = new Blob(parts, { type: `${mime};charset=utf-8;` });
  const text = parts.length < 5 ? parts.join('') : '';
  return { blob, text, rows: effectiveR1 - r0 + 1, cells: totalCells };
}

/** Reads one cell from a tile snapshot, returning a CellValue. */
function readCell(
  tile: TileLike,
  row: number,
  col: number,
): CellValue {
  if (tile.values !== undefined) {
    const width = tile.range.c1 - tile.range.c0 + 1;
    const idx = (row - tile.range.r0) * width + (col - tile.range.c0);
    return tile.values[idx] ?? EMPTY;
  }
  return tileCell(tile as Parameters<typeof tileCell>[0], row, col);
}

interface TileLike {
  range: Range;
  rows: number;
  cols: number;
  values?: readonly CellValue[];
  kinds?: Uint8Array;
  numbers?: Float64Array;
  codes?: Int32Array;
  strings?: readonly string[];
  styleIds?: Int32Array;
}

function formatCell(v: CellValue): string {
  if (v.k === 'empty') return '';
  if (v.k === 'text') return v.v;
  if (v.k === 'number') {
    // If number represents an Excel serial date range (>20,000 & integer), format as YYYY-MM-DD or return number
    if (Number.isInteger(v.v)) return v.v.toString();
    return String(v.v);
  }
  if (v.k === 'bool') return v.v ? 'TRUE' : 'FALSE';
  if (v.k === 'error') return String(v.v);
  return '';
}

/** Joins one record's fields with the format-specific delimiter and quoting. */
function formatLine(fields: readonly string[], format: ExportFormat): string {
  if (format === 'jsonl') {
    const obj: Record<string, string> = {};
    fields.forEach((f, i) => {
      if (f.length > 0) obj[`c${i}`] = f;
    });
    return JSON.stringify(obj);
  }
  const delim = format === 'tsv' ? '\t' : ',';
  return fields.map((f) => quote(f, delim)).join(delim);
}

function quote(text: string, delimiter: string): string {
  const needsQuote =
    text.length === 0 ||
    text.includes(delimiter) ||
    text.includes('"') ||
    text.includes('\n') ||
    text.includes('\r');
  if (!needsQuote) return text;
  return `"${text.replace(/"/g, '""')}"`;
}

/** Convenience: writes the export to a downloadable Blob URL and triggers a click. */
export function downloadExport(content: Blob | string, filename: string, format: ExportFormat): void {
  const mime =
    format === 'jsonl' ? 'application/x-ndjson' : format === 'tsv' ? 'text/tab-separated-values' : 'text/csv';
  const blob = content instanceof Blob ? content : new Blob([content], { type: `${mime};charset=utf-8;` });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
